import {
  CategoryOption, LevelOption,
  PLSession,
  SubjectSummary,
  SubjectOption, StatementSelectionsData, SelectedNeedsData, AlignedGoalsResponse, AlignedGoals
} from '@/presentlevels/presentlevels_types.ts'
import { sendHTTPRequest } from "@/util/http.ts";
import { goalbookUrls } from "@/util/links.js";
import { getCookie, getHandleRequest } from '@/authentication/request_handler.ts'
import { Standard } from '@/standards/standard_types.ts'

export async function getPresentlevelsSession(sessionUUID: string): Promise<PLSession> {
  try {
    const requestFn = async () => {
      return await fetch(`${goalbookUrls.presentlevels}/api/v2/pl_sessions/${sessionUUID}`, {
        method: 'GET',
        credentials: 'include'
      })
    }
    const requestHandler = getHandleRequest()
    return await requestHandler({ requestFn }) as PLSession
  } catch (e) {
    throw new Error('Error Getting Session', { cause: e })
  }
}

export async function postPresentlevelsSession(body: {
  student_name: string,
  enrolled_grade_uuid: string,
  selected_subject_uuids: string[]
}): Promise<PLSession> {
  const requestFn = async () => {
    const gbcs = getCookie('gbcs')
    return await fetch(`${goalbookUrls.presentlevels}/api/v2/pl_sessions/`,{
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
        'X-CSRF-Token': gbcs
      },
      body: JSON.stringify(body),
      credentials: 'include'
    })
  }
  try {
    const requestHandler = getHandleRequest()
    return await requestHandler({ requestFn }) as PLSession
  } catch (e) {
    throw new Error('Error Creating Session', { cause: e })
  }
}

export async function getSubjectSummariesForSession(sessionUUID: string): Promise<SubjectSummary[]> {
  try {
    const options = new URLSearchParams({ pl_session_uuid: sessionUUID})
    const requestFn = async () => {
      return await fetch(`${goalbookUrls.presentlevels}/api/v2/summaries?${options.toString()}`, {
        method: 'GET',
        credentials: 'include'
      })
    }
    const requestHandler = getHandleRequest()
    return await requestHandler({ requestFn }) as SubjectSummary[]
  } catch (e) {
    throw new Error('Error Getting Subject Summaries', { cause: e })
  }
}

export async function getSubjectSummaryByUUID(summaryUUID: string): Promise<SubjectSummary> {
  try {
    const requestFn = async () => {
      return await fetch(`${goalbookUrls.presentlevels}/api/v2/summaries/${summaryUUID}`, {
        method: 'GET',
        credentials: 'include'
      })
    }
    const requestHandler = getHandleRequest()
    return await requestHandler({ requestFn }) as SubjectSummary
  } catch (e) {
    throw new Error('Error Getting Subject Summary', { cause: e })
  }
}

export async function postSubjectSummary(body: {
  student_name: string,
  subject_uuid: string,
  subject_label: string,
  grade_uuid: string,
  grade_label: string,
  pl_session_uuid: string
  statement_text?: string,
  aligned_goals?: AlignedGoals
  standards?: Standard[]
}): Promise<SubjectSummary> {
  try {
    const requestFn = async () => {
      const gbcs = getCookie('gbcs')
      return await fetch(`${goalbookUrls.presentlevels}/api/v2/summaries/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
          'X-CSRF-Token': gbcs
        },
        body: JSON.stringify(body),
        credentials: 'include'
      })
    }
    const requestHandler = getHandleRequest()
    return await requestHandler({ requestFn }) as SubjectSummary
  } catch (e) {
    throw new Error('Error Creating Subject Summary', { cause: e })
  }
}

export async function postCategorySelections(args: StatementSelectionsData): Promise<SubjectSummary> {
  try {
    const requestFn = async () => {
      const gbcs = getCookie('gbcs')
      const queryParams = new URLSearchParams()
      if (args.fetchGoals !== undefined) {
        queryParams.append('fetchgoals', String(args.fetchGoals))
      }
      if (args.beta !== undefined) {
        queryParams.append('beta', String(args.beta))
      }
      const queryString = queryParams.toString() ? `?${queryParams.toString()}` : ''
      return await fetch(`${goalbookUrls.presentlevels}/api/v2/summaries/${args.summaryUUID}/statement/${queryString}`,{
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
          'X-CSRF-Token': gbcs
        },
        body: JSON.stringify({
          category_label: args.categoryLabel,
          category_uuid: args.categoryUUID,
          strengths: args.strengths,
          needs: args.needs,
          plw_trace: args.plw_trace,
          save_to_summary: args.saveToSummary,
          statement_text: args.statementText,
          aligned_goals: args.alignedGoals,
        }),
        credentials: 'include'
      })
    }
    const requestHandler = getHandleRequest()
    return await requestHandler({ requestFn }) as SubjectSummary
  } catch (e) {
    throw new Error('Error Submitting Category Selections', { cause: e })
  }
}

export async function putSubjectSummary(summaryUUID: string, fieldsToUpdate: Partial<SubjectSummary>): Promise<SubjectSummary> {
  try {
    const gbcs = getCookie('gbcs')
    const response = await sendHTTPRequest({
      url: `${goalbookUrls.presentlevels}/api/v2/summaries/${summaryUUID}`,
      method: 'PUT',
      jsonBody: fieldsToUpdate,
      addlHeaders: {
        'X-CSRF-Token': gbcs
      },
      includeCredentials: true
    })
    return await response.json() as SubjectSummary
  } catch (e) {
    throw new Error('Error Updating Summary', { cause: e })
  }
}

export async function putSummaryStatementText(summaryUUID: string, fieldsToUpdate: Partial<SubjectSummary>): Promise<SubjectSummary> {
  return putSubjectSummary(summaryUUID, fieldsToUpdate)
}

export async function getSubjectOptions(): Promise<SubjectOption[]> {
  try {
    const requestFn = async () => {
      const gbcs = getCookie('gbcs')
      return await fetch(`${goalbookUrls.presentlevels}/api/v2/subjects/`, {
        method: 'GET',
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
          'X-CSRF-Token': gbcs
        },
        credentials: 'include'
      })
    }
    const requestHandler = getHandleRequest()
    return await requestHandler({ requestFn }) as SubjectOption[]
  } catch (e) {
    throw new Error('Error Getting Subject Options', { cause: e })
  }
}

export async function getLevelOptions(): Promise<LevelOption[]> {
  try {
    const response = await sendHTTPRequest({
      url: `${goalbookUrls.presentlevels}/api/v2/levels/`,
      method: 'GET',
      includeCredentials: true
    })
    return await response.json() as LevelOption[]
  } catch (e) {
    throw new Error('Error Getting Level Options', { cause: e })
  }
}

export async function getSubjectCategoriesWithSkills(subjectUUID: string): Promise<CategoryOption[]> {
  try {
    const response = await sendHTTPRequest({
      url: `${goalbookUrls.presentlevels}/api/v2/subjects/${subjectUUID}`,
      method: 'GET',
      includeCredentials: true
    })
    return await response.json() as CategoryOption[]
  } catch (e) {
    throw new Error('Error Getting Subject Categories', { cause: e })
  }
}

export async function deleteSubjectSummary(summaryUUID: string): Promise<void> {
  try {
    const requestFn = async () => {
      const gbcs = getCookie('gbcs')
      return await fetch(`${goalbookUrls.presentlevels}/api/v2/summaries/${summaryUUID}`,{
        method: 'DELETE',
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
          'X-CSRF-Token': gbcs
        },
        credentials: 'include'
      })
    }
    const requestHandler = getHandleRequest()
    await requestHandler({ requestFn })
  } catch (e) {
    throw new Error('Error deleting subject summary', { cause: e })
  }
}

export async function getAlignedGoalsForSummary(
  summaryUUID: string,
  body: {
    subject_label: string,
    grade_label: string,
    category_label: string,
    needs: SelectedNeedsData[]
  }
): Promise<AlignedGoalsResponse> {
  try {
    const requestFn = async () => {
      const gbcs = getCookie('gbcs')
      return await fetch(`${goalbookUrls.presentlevels}/api/v2/summaries/${summaryUUID}/aligned_goals/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
          'X-CSRF-Token': gbcs
        },
        body: JSON.stringify(body),
        credentials: 'include'
      })
    }
    const requestHandler = getHandleRequest()
    return await requestHandler({ requestFn }) as AlignedGoalsResponse
  } catch (e) {
    throw new Error('Error Getting Aligned Goals For Summary', { cause: e })
  }
}
